import { ArrowLeft, Tag } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { ClaudiaIcon } from "@/components/claudia-icon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useCreateClarification } from "@/features/clarification/hooks";
import {
  formatFieldName,
  mapEntityTypeToBackend,
} from "@/features/clarification/utils/field-utils";
import {
  useRightSidebarClarification,
  useRightSidebarEntity,
  useRightSidebarProject,
} from "@/features/project/contexts";
import { clarificationInputDefaults, MessageRichTextInput } from "@/features/rich-text";
import { useCurrentUser } from "@/hooks/use-current-user";
import { trackClarificationEvent } from "@/lib/analytics/domain";

export function ClarificationCreateForm({ onBack }: { onBack: () => void }) {
  const { projectId } = useRightSidebarProject();
  const { entityType, entityId, entityName } = useRightSidebarEntity();
  const { clarificationCreateFieldName, setClarificationCreateFieldName, openClarificationThread } =
    useRightSidebarClarification();

  const currentUser = useCurrentUser();
  const createClarification = useCreateClarification();

  const [title, setTitle] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (titleText: string = title): Promise<boolean> => {
    const trimmedTitle = titleText.trim();
    if (!trimmedTitle) {
      toast.error("Please enter a title for the clarification.");
      return false;
    }

    setIsSubmitting(true);
    try {
      const clarificationId = await createClarification({
        projectId,
        title: trimmedTitle,
        status: "open",
        createdBy: currentUser?._id,
        // Entity scoping — subjectType/subjectId are now required
        subjectType: entityType ? mapEntityTypeToBackend(entityType) : "project",
        subjectId: entityId ?? (projectId as string),
        ...(clarificationCreateFieldName && { fieldId: clarificationCreateFieldName }),
      });

      toast.success("Clarification created");
      trackClarificationEvent("clarification_created", {
        surface: "right_panel",
        project_id: projectId,
        scope_kind: entityType && entityId ? "entity" : "project",
        entity_type_key: entityType ?? undefined,
        entity_id: entityId ?? undefined,
        has_initial_message: false,
      });
      setClarificationCreateFieldName(null);
      openClarificationThread(clarificationId, { replace: true });
      return true;
    } catch {
      toast.error("Failed to create clarification. Please try again.");
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setClarificationCreateFieldName(null);
    onBack();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b shrink-0">
        <button
          type="button"
          onClick={handleCancel}
          className="text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Back to list"
        >
          <ArrowLeft className="size-4" />
        </button>
        <h3 className="text-sm font-medium">New Clarification</h3>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {/* Context badges (auto-filled from entity context) */}
        {entityType && (
          <div className="flex flex-wrap gap-1.5">
            <Badge variant="outline" className="gap-1">
              <Tag className="size-3" />
              {entityName ?? entityType}
            </Badge>
            {clarificationCreateFieldName && (
              <Badge variant="secondary">{formatFieldName(clarificationCreateFieldName)}</Badge>
            )}
          </div>
        )}

        {/* Title */}
        <div className="space-y-1.5">
          <Label>Title</Label>
          <MessageRichTextInput
            {...clarificationInputDefaults}
            placeholder="What needs clarification?"
            autoFocus
            disabled={isSubmitting}
            mentionProjectId={projectId}
            voiceInput={{ projectId, source: "clarification", surface: "right_panel" }}
            onContentChange={(payload) => setTitle(payload.plainText)}
            onSubmit={(payload) => handleSubmit(payload.plainText)}
          />
        </div>

        {/* Owner selector */}
        <div className="space-y-1.5">
          <Label>Assignee</Label>
          <div className="border-input bg-muted/40 flex h-9 items-center gap-2 rounded-md border px-3 text-sm">
            <ClaudiaIcon className="size-4 text-muted-foreground" aria-hidden />
            <span>Claudia</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 px-4 py-3 border-t shrink-0">
        <Button variant="outline" className="flex-1" onClick={handleCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          className="flex-1"
          onClick={() => void handleSubmit()}
          disabled={isSubmitting || !title.trim()}
        >
          {isSubmitting ? "Creating..." : "Create"}
        </Button>
      </div>
    </div>
  );
}
